import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import ThreeViewer from '@/components/ThreeViewer';
import ImageOverlay from '@/components/ImageOverlay';
import DepthAnalysis from '@/components/DepthAnalysis';
import { BarChart3, Download, Calendar, Activity, TrendingUp, AlertCircle, Image as ImageIcon, Layers, Brain, Video } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MetricCard = ({ title, value, unit, icon: Icon, color = "text-blue-600", isPending = false }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <p className={`text-2xl font-bold ${isPending ? 'text-gray-400' : ''}`}>
            {value}
            {unit && !isPending && <span className="text-sm font-normal text-gray-500 ml-1">{unit}</span>}
          </p>
          {isPending && (
            <p className="text-xs text-gray-400 mt-1">Awaiting AI analysis</p>
          )}
        </div>
        {Icon && (
          <div className={`w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center ${isPending ? 'text-gray-400' : color}`}>
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

const ClassificationChart = ({ classifications }) => {
  if (!classifications || Object.keys(classifications).length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No classification data available</p>
      </div>
    );
  }

  const total = Object.values(classifications).reduce((sum, count) => sum + count, 0);
  
  const colors = {
    'Superficial (First-degree)': 'bg-green-400',
    'Partial thickness (Second-degree)': 'bg-yellow-400', 
    'Full thickness (Third-degree)': 'bg-red-400'
  };

  return (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-900">Burn Classification Distribution</h4>
      <div className="space-y-3">
        {Object.entries(classifications).map(([type, count]) => {
          const percentage = ((count / total) * 100).toFixed(1);
          return (
            <div key={type} className="flex items-center gap-3">
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium">{type}</span>
                  <span className="text-gray-500">{percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${colors[type] || 'bg-gray-400'}`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
              <span className="text-sm text-gray-600 min-w-[3rem]">{count} areas</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const AnalysisView = ({ sessionId, analysisData, onComplete }) => {
  const [modelData, setModelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showImageOverlay, setShowImageOverlay] = useState(false);
  const [advancedAnalysisLoading, setAdvancedAnalysisLoading] = useState(false);
  const [advancedAnalysisResults, setAdvancedAnalysisResults] = useState(null);
  const [showAdvancedResults, setShowAdvancedResults] = useState(false);
  const [existingAdvancedResults, setExistingAdvancedResults] = useState(null);

  useEffect(() => {
    if (sessionId) {
      load3DModel();
      loadExistingAdvancedResults();
    }
  }, [sessionId]);

  const load3DModel = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/sessions/${sessionId}/3d-model`);
      setModelData(response.data.model);
    } catch (error) {
      console.error('Failed to load 3D model:', error);
      let errorMessage = 'Failed to load 3D visualization.';
      
      if (error.response?.status === 403) {
        errorMessage = 'Access denied for 3D model. Please check authentication.';
      } else if (error.response?.status === 404) {
        errorMessage = 'No 3D model available yet. Try running Advanced AI Analysis first.';
      } else if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network')) {
        errorMessage = 'Network connection issue. Please check your internet connection.';
      } else {
        errorMessage = 'Failed to load 3D visualization. The model may still be generating or there may be connection issues.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadExistingAdvancedResults = async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}/advanced-analysis`);
      if (response.data.results && response.data.results.length > 0) {
        // Find the most recent quantitative analysis
        const quantitativeResults = response.data.results.filter(r => 
          r.analysis_type === 'skinopathy_quantitative' && 
          r.result?.analysis_data?.has_burn
        );
        
        if (quantitativeResults.length > 0) {
          // Use the most recent result
          const latestResult = quantitativeResults.sort((a, b) => 
            new Date(b.created_at) - new Date(a.created_at)
          )[0];
          
          setExistingAdvancedResults(latestResult);
          console.log('📊 Found existing Advanced Analysis results for 3D display consistency');
        }
      }
    } catch (error) {
      console.log('No existing Advanced Analysis results found (this is normal for new sessions)');
    }
  };

  const handleExportPLY = async () => {
    try {
      console.log('🔄 Starting PLY export for session:', sessionId);
      
      if (!sessionId) {
        throw new Error('No session ID available');
      }

      const url = `${API}/sessions/${sessionId}/download/ply`;
      console.log('📡 Making request to:', url);
      
      const response = await axios.get(url, { 
        responseType: 'blob',
        headers: {
          'Accept': 'application/octet-stream'
        },
        timeout: 30000 // 30 second timeout
      });
      
      console.log('✅ PLY download response:', {
        status: response.status,
        size: response.data.size,
        type: response.data.type
      });
      
      if (response.data.size === 0) {
        throw new Error('PLY file is empty');
      }
      
      // Create and trigger download
      const downloadUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `SKINOPATHY_burn_model_${sessionId}.ply`;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup URL after download
      setTimeout(() => {
        window.URL.revokeObjectURL(downloadUrl);
        console.log('🗑️ Cleaned up download URL');
      }, 1000);
      
      console.log('✅ PLY export completed successfully');
      alert('PLY file downloaded successfully!');
      
    } catch (error) {
      console.error('❌ PLY export failed:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Failed to export PLY file: ${errorMsg}`);
    }
  };

  const handleExport3DModel = async () => {
    try {
      console.log('🔄 Starting 3D model export for session:', sessionId);
      
      if (!sessionId) {
        throw new Error('No session ID available');
      }

      const url = `${API}/sessions/${sessionId}/3d-model`;
      console.log('📡 Fetching 3D model data from:', url);
      
      const response = await axios.get(url, {
        timeout: 30000 // 30 second timeout
      });
      
      console.log('✅ 3D model data received:', {
        vertices: response.data.model?.vertex_count,
        metadata: Object.keys(response.data.metadata || {})
      });
      
      // Create comprehensive JSON export
      const exportData = {
        skinopathy_export: {
          session_id: sessionId,
          export_date: new Date().toISOString(),
          export_type: "SKINOPATHY_3D_Model",
          version: "1.0"
        },
        model_data: response.data.model,
        metadata: response.data.metadata,
        analysis_summary: analysisData?.metrics ? {
          surface_area_cm2: analysisData.metrics.surface_area_cm2,
          volume_cm3: analysisData.metrics.volume_cm3,
          tbsa_percentage: analysisData.metrics.tbsa_percentage,
          healing_stage: analysisData.metrics.healing_stage,
          confidence: analysisData.metrics.confidence
        } : null
      };
      
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      console.log('💾 Creating download for JSON file, size:', dataBlob.size, 'bytes');
      
      const downloadUrl = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `SKINOPATHY_3d_model_${sessionId}.json`;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => {
        window.URL.revokeObjectURL(downloadUrl);
        console.log('🗑️ Cleaned up download URL');
      }, 1000);
      
      console.log('✅ 3D model export completed successfully');
      alert('3D model JSON file downloaded successfully!');
      
    } catch (error) {
      console.error('❌ 3D model export failed:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Failed to export 3D model: ${errorMsg}`);
    }
  };

  const handleAdvancedAnalysis = async () => {
    try {
      setAdvancedAnalysisLoading(true);
      console.log('🧠 Starting advanced AI analysis for session:', sessionId);
      
      if (!sessionId) {
        throw new Error('No session ID available');
      }

      // Configure timeout for AI API calls (5 minutes each for vision processing)
      const timeoutConfig = { timeout: 300000 };
      
      console.log('🔄 Starting quantitative analysis first...');
      const quantResponse = await axios.post(
        `${API}/sessions/${sessionId}/advanced-analysis/quantitative`, 
        {}, 
        timeoutConfig
      );
      
      console.log('✅ Quantitative analysis completed, starting mesh overlay generation...');
      const meshResponse = await axios.post(
        `${API}/sessions/${sessionId}/advanced-analysis/mesh`, 
        {}, 
        timeoutConfig
      );
      
      const results = {
        mesh: meshResponse.data,
        quantitative: quantResponse.data,
        timestamp: new Date().toISOString()
      };
      
      setAdvancedAnalysisResults(results);
      // Also update existing results to ensure data consistency across components
      setExistingAdvancedResults(quantResponse.data);
      
      console.log('✅ Advanced analysis completed successfully - data consistency updated');
      
    } catch (error) {
      console.error('❌ Advanced analysis failed:', error);
      let errorMsg = 'Unknown error';
      
      if (error.code === 'ECONNABORTED') {
        errorMsg = 'Analysis timed out. AI processing may take up to 2 minutes per step.';
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      alert(`Advanced analysis failed: ${errorMsg}\n\nNote: AI analysis can take 1-2 minutes. Please wait for completion.`);
    } finally {
      setAdvancedAnalysisLoading(false);
    }
  };

  const handleVideoGeneration = () => {
    // Placeholder for future video generation functionality
    alert('Video Generation feature will be implemented in a future update. This will create a rotating 3D video of the burn model using Gemini Veo.');
  };

  // Use Advanced Analysis results if available, otherwise show empty/zero values
  const getDisplayMetrics = () => {
    // First check if we have current Advanced Analysis results with burn detected
    if (advancedAnalysisResults?.quantitative?.result?.analysis_data?.has_burn) {
      const advancedData = advancedAnalysisResults.quantitative.result.analysis_data;
      return {
        ...analysisData?.metrics,
        surface_area_cm2: advancedData.SurfaceArea_cm2,
        volume_cm3: advancedData.Volume_cm3,
        max_depth_mm: advancedData.MaxDepth_mm,
        avg_depth_mm: advancedData.AvgDepth_mm,
        tbsa_percentage: advancedData.TBSA_percent,
        perimeter_cm: advancedData.Perimeter_cm,
        classifications: {
          'Superficial (First-degree)': advancedData.BurnClassification?.Superficial || 0,
          'Partial thickness (Second-degree)': advancedData.BurnClassification?.PartialThickness || 0,
          'Full thickness (Third-degree)': advancedData.BurnClassification?.FullThickness || 0
        },
        healing_stage: advancedData.ClinicalSummary || 'Assessment complete',
        data_source: 'Current Advanced AI Analysis',
        confidence: 95, // High confidence from AI analysis
        analyzed_images: analysisData?.metrics?.analyzed_images || 1,
        created_at: new Date().toISOString()
      };
    }
    // Check if current Advanced Analysis found no burn
    else if (advancedAnalysisResults?.quantitative?.result?.analysis_data && !advancedAnalysisResults.quantitative.result.analysis_data.has_burn) {
      return {
        surface_area_cm2: 0,
        volume_cm3: 0,
        max_depth_mm: 0,
        avg_depth_mm: 0,
        tbsa_percentage: 0,
        perimeter_cm: 0,
        classifications: {
          'Superficial (First-degree)': 0,
          'Partial thickness (Second-degree)': 0,
          'Full thickness (Third-degree)': 0
        },
        healing_stage: 'No burn detected',
        data_source: 'Current Advanced AI Analysis',
        confidence: 95,
        analyzed_images: analysisData?.metrics?.analyzed_images || 1,
        created_at: new Date().toISOString()
      };
    }
    // Then check existing Advanced Analysis results from previous runs with burn
    else if (existingAdvancedResults?.result?.analysis_data?.has_burn) {
      const advancedData = existingAdvancedResults.result.analysis_data;
      return {
        ...analysisData?.metrics,
        surface_area_cm2: advancedData.SurfaceArea_cm2,
        volume_cm3: advancedData.Volume_cm3,
        max_depth_mm: advancedData.MaxDepth_mm,
        avg_depth_mm: advancedData.AvgDepth_mm,
        tbsa_percentage: advancedData.TBSA_percent,
        perimeter_cm: advancedData.Perimeter_cm,
        classifications: {
          'Superficial (First-degree)': advancedData.BurnClassification?.Superficial || 0,
          'Partial thickness (Second-degree)': advancedData.BurnClassification?.PartialThickness || 0,
          'Full thickness (Third-degree)': advancedData.BurnClassification?.FullThickness || 0
        },
        healing_stage: advancedData.ClinicalSummary || 'Previous assessment',
        data_source: 'Previous Advanced AI Analysis',
        confidence: 95,
        analyzed_images: analysisData?.metrics?.analyzed_images || 1,
        created_at: existingAdvancedResults.created_at || new Date().toISOString()
      };
    }
    // Check if existing Advanced Analysis found no burn
    else if (existingAdvancedResults?.result?.analysis_data && !existingAdvancedResults.result.analysis_data.has_burn) {
      return {
        surface_area_cm2: 0,
        volume_cm3: 0,
        max_depth_mm: 0,
        avg_depth_mm: 0,
        tbsa_percentage: 0,
        perimeter_cm: 0,
        classifications: {
          'Superficial (First-degree)': 0,
          'Partial thickness (Second-degree)': 0,
          'Full thickness (Third-degree)': 0
        },
        healing_stage: 'No burn detected',
        data_source: 'Previous Advanced AI Analysis',
        confidence: 95,
        analyzed_images: analysisData?.metrics?.analyzed_images || 1,
        created_at: existingAdvancedResults.created_at || new Date().toISOString()
      };
    }
    // Otherwise show placeholder values until Advanced AI analysis is run
    else {
      return {
        surface_area_cm2: '--',
        volume_cm3: '--', 
        max_depth_mm: '--',
        avg_depth_mm: '--',
        tbsa_percentage: '--',
        perimeter_cm: '--',
        classifications: {},
        healing_stage: 'Run Advanced AI Analysis to get detailed metrics',
        data_source: 'Awaiting Advanced AI Analysis',
        confidence: 0,
        analyzed_images: analysisData?.metrics?.analyzed_images || 0,
        created_at: new Date().toISOString()
      };
    }
  };

  const metrics = getDisplayMetrics();

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold mb-2">Loading 3D Analysis</h3>
              <p className="text-gray-600">Generating 3D visualization and metrics...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Advanced Analysis Section - Runs First */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="w-6 h-6 text-blue-600" />
            Advanced AI 3D Burn Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 mb-2">
                Run comprehensive AI analysis for detailed burn assessment and 3D mesh visualization
              </p>
              <p className="text-sm text-gray-500">
                This analysis provides quantitative metrics, burn classification, and generates mesh overlays
              </p>
            </div>
            <div>
              <Button 
                variant="outline" 
                onClick={handleAdvancedAnalysis}
                disabled={advancedAnalysisLoading}
                data-testid="advanced-analysis-button"
                className="relative"
                title={advancedAnalysisLoading ? "AI analysis in progress (may take 1-2 minutes)" : "Run advanced AI burn analysis"}
              >
                {advancedAnalysisLoading ? (
                  <>
                    <div className="w-4 h-4 mr-2 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
                    AI Processing...
                  </>
                ) : (
                  <>
                    <Brain className="w-4 h-4 mr-2" />
                    Advanced Analysis
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Display Advanced Analysis Results when available */}
          {advancedAnalysisResults && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-4 text-lg">Advanced Analysis Results</h4>
              
              {/* Mesh Analysis Results */}
              {advancedAnalysisResults.mesh && (
                <div className="mb-6">
                  <h5 className="font-medium mb-3 flex items-center gap-2">
                    <Layers className="w-5 h-5 text-green-600" />
                    3D Mesh Analysis
                  </h5>
                  <div className="bg-white rounded-lg p-4">
                    <div className="text-sm space-y-2 mb-4">
                      <div><strong>Status:</strong> {advancedAnalysisResults.mesh.result?.success ? 'Success' : 'Failed'}</div>
                      <div><strong>Burn Detected:</strong> {advancedAnalysisResults.mesh.result?.has_burn ? 'Yes' : 'No'}</div>
                    </div>
                    
                    {advancedAnalysisResults.mesh.result?.overlay_filename && (
                      <div>
                        <h6 className="font-medium mb-2">Generated 3D Mesh Overlay:</h6>
                        <img 
                          src={`${API}/sessions/${sessionId}/images/${advancedAnalysisResults.mesh.result.overlay_filename}`}
                          alt="3D Mesh Overlay"
                          className="max-w-full h-auto rounded border shadow-sm"
                          onError={(e) => {
                            console.error('Failed to load mesh overlay image');
                            e.target.style.display = 'none';
                          }}
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          SKINOPATHY-generated 3D mesh overlay with burn area analysis
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Quantitative Analysis Results */}
              {advancedAnalysisResults.quantitative && (
                <div>
                  <h5 className="font-medium mb-3 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                    Quantitative Analysis
                  </h5>
                  
                  {advancedAnalysisResults.quantitative.result?.analysis_data && advancedAnalysisResults.quantitative.result.analysis_data.has_burn ? (
                    <div className="bg-white rounded-lg p-4 space-y-4">
                      
                      {/* Quantitative Measurements Table */}
                      <div>
                        <h6 className="font-medium mb-2 text-blue-800">Quantitative Burn Analysis</h6>
                        <div className="overflow-x-auto">
                          <table className="min-w-full bg-white border border-gray-200 text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left border-b">Metric</th>
                                <th className="px-3 py-2 text-left border-b">Value</th>
                                <th className="px-3 py-2 text-left border-b">Units</th>
                                <th className="px-3 py-2 text-left border-b">Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-b">
                                <td className="px-3 py-2 font-medium">Surface Area</td>
                                <td className="px-3 py-2">{advancedAnalysisResults.quantitative.result.analysis_data.SurfaceArea_cm2}</td>
                                <td className="px-3 py-2">cm²</td>
                                <td className="px-3 py-2">Total visible affected area</td>
                              </tr>
                              <tr className="border-b">
                                <td className="px-3 py-2 font-medium">Volume</td>
                                <td className="px-3 py-2">{advancedAnalysisResults.quantitative.result.analysis_data.Volume_cm3}</td>
                                <td className="px-3 py-2">cm³</td>
                                <td className="px-3 py-2">Estimated wound cavity volume</td>
                              </tr>
                              <tr className="border-b">
                                <td className="px-3 py-2 font-medium">Max Depth</td>
                                <td className="px-3 py-2">{advancedAnalysisResults.quantitative.result.analysis_data.MaxDepth_mm}</td>
                                <td className="px-3 py-2">mm</td>
                                <td className="px-3 py-2">Maximum visible tissue depression</td>
                              </tr>
                              <tr className="border-b">
                                <td className="px-3 py-2 font-medium">Avg Depth</td>
                                <td className="px-3 py-2">{advancedAnalysisResults.quantitative.result.analysis_data.AvgDepth_mm}</td>
                                <td className="px-3 py-2">mm</td>
                                <td className="px-3 py-2">Average surface depression</td>
                              </tr>
                              <tr className="border-b">
                                <td className="px-3 py-2 font-medium">Perimeter</td>
                                <td className="px-3 py-2">{advancedAnalysisResults.quantitative.result.analysis_data.Perimeter_cm}</td>
                                <td className="px-3 py-2">cm</td>
                                <td className="px-3 py-2">Boundary length of affected region</td>
                              </tr>
                              <tr>
                                <td className="px-3 py-2 font-medium">TBSA Percentage</td>
                                <td className="px-3 py-2">{advancedAnalysisResults.quantitative.result.analysis_data.TBSA_percent}%</td>
                                <td className="px-3 py-2">%</td>
                                <td className="px-3 py-2">Total Body Surface Area affected</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Burn Classification Table */}
                      {advancedAnalysisResults.quantitative.result.analysis_data.BurnClassification && (
                        <div>
                          <h6 className="font-medium mb-2 text-purple-800">Burn Classification Distribution</h6>
                          <div className="overflow-x-auto">
                            <table className="min-w-full bg-white border border-gray-200 text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left border-b">Burn Type</th>
                                  <th className="px-3 py-2 text-left border-b">Percentage</th>
                                  <th className="px-3 py-2 text-left border-b">Clinical Notes</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr className="border-b">
                                  <td className="px-3 py-2 font-medium">Superficial (1°)</td>
                                  <td className="px-3 py-2">{advancedAnalysisResults.quantitative.result.analysis_data.BurnClassification.Superficial}%</td>
                                  <td className="px-3 py-2">Red, intact epidermis, no blisters</td>
                                </tr>
                                <tr className="border-b">
                                  <td className="px-3 py-2 font-medium">Partial-thickness (2°)</td>
                                  <td className="px-3 py-2">{advancedAnalysisResults.quantitative.result.analysis_data.BurnClassification.PartialThickness}%</td>
                                  <td className="px-3 py-2">Glossy red, moist, blistering, viable dermis</td>
                                </tr>
                                <tr>
                                  <td className="px-3 py-2 font-medium">Full-thickness (3°)</td>
                                  <td className="px-3 py-2">{advancedAnalysisResults.quantitative.result.analysis_data.BurnClassification.FullThickness}%</td>
                                  <td className="px-3 py-2">Charred, leathery, waxy white or black regions</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Clinical Summary */}
                      {advancedAnalysisResults.quantitative.result.analysis_data.ClinicalSummary && (
                        <div>
                          <h6 className="font-medium mb-2 text-red-800">Clinical Summary</h6>
                          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm">
                            <p className="font-medium text-red-900">{advancedAnalysisResults.quantitative.result.analysis_data.ClinicalSummary}</p>
                          </div>
                        </div>
                      )}

                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-4">
                      <div className="bg-green-50 border border-green-200 rounded p-3 text-sm">
                        <p className="text-green-800 font-medium">✅ No burn or injury detected in the analyzed image.</p>
                        <p className="text-green-700 mt-1">The AI analysis did not identify any visible burns, wounds, or lesions requiring medical assessment.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 3D Burn Analysis Results Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-green-600" />
            3D Burn Analysis Results
            {metrics.data_source === 'Awaiting Advanced AI Analysis' && (
              <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-md">
                ⏳ Waiting for Advanced AI Analysis
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 mb-2">
                {metrics.data_source === 'Awaiting Advanced AI Analysis' ? (
                  <>Analysis ready for {metrics.analyzed_images || 0} images - Run Advanced AI Analysis for detailed metrics</>
                ) : (
                  <>Analysis completed successfully with {metrics.analyzed_images || 0} images</>
                )}
                {(metrics.data_source?.includes('Advanced') && metrics.data_source !== 'Awaiting Advanced AI Analysis') && (
                  <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md">
                    📊 {metrics.data_source}
                  </span>
                )}
              </p>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(metrics.created_at || Date.now()).toLocaleString()}
                </span>
                <span className="flex items-center gap-1">
                  <Activity className="w-4 h-4" />
                  Confidence: {metrics.confidence || 0}%
                </span>
                {metrics.data_source === 'Awaiting Advanced AI Analysis' && (
                  <span className="flex items-center gap-1 text-blue-600 font-medium">
                    <Brain className="w-4 h-4" />
                    Advanced AI Analysis required for detailed metrics
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button 
                variant="outline" 
                onClick={() => setShowImageOverlay(true)}
                data-testid="image-overlay-button"
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                Image Overlay
              </Button>
              <Button 
                variant="outline" 
                onClick={handleExport3DModel}
                data-testid="export-3d-model-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Export 3D Model
              </Button>
              <Button 
                variant="outline" 
                onClick={handleExportPLY}
                data-testid="export-ply-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Export PLY
              </Button>
              <Button 
                variant="outline" 
                onClick={handleVideoGeneration}
                data-testid="video-generation-button"
              >
                <Video className="w-4 h-4 mr-2" />
                Video Generation
              </Button>
              {onComplete && (
                <Button onClick={() => onComplete('temporal')}>
                  <TrendingUp className="w-4 h-4 mr-2" />
                  View Temporal Analysis
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          title="Surface Area"
          value={metrics.surface_area_cm2 || '--'}
          unit="cm²"
          icon={BarChart3}
          color="text-blue-600"
          isPending={metrics.data_source === 'Awaiting Advanced AI Analysis'}
        />
        <MetricCard
          title="Volume"
          value={metrics.volume_cm3 || '--'}
          unit="cm³"
          icon={BarChart3}
          color="text-purple-600"
          isPending={metrics.data_source === 'Awaiting Advanced AI Analysis'}
        />
        <MetricCard
          title="Max Depth"
          value={metrics.max_depth_mm || '--'}
          unit="mm"
          icon={BarChart3}
          color="text-red-600"
          isPending={metrics.data_source === 'Awaiting Advanced AI Analysis'}
        />
        <MetricCard
          title="TBSA"
          value={metrics.tbsa_percentage || '--'}
          unit="%"
          icon={BarChart3}
          color="text-orange-600"
          isPending={metrics.data_source === 'Awaiting Advanced AI Analysis'}
        />
      </div>

      {/* 3D Visualization */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {error ? (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2 text-red-700">Visualization Error</h3>
                  <p className="text-gray-600 mb-4">{error}</p>
                  {error?.includes('No 3D model available') ? (
                    <div className="text-sm text-blue-600 bg-blue-50 p-3 rounded mb-4">
                      <p className="font-medium">💡 Quick Analysis Mode</p>
                      <p>For single image quick analysis, 3D reconstruction may not be available. Use "Advanced AI Analysis" for detailed burn assessment.</p>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 mb-4">
                      <p>• Check your internet connection</p>
                      <p>• Ensure you've completed image upload and processing</p>
                      <p>• Try running Advanced AI Analysis first</p>
                    </div>
                  )}
                  <Button variant="outline" onClick={load3DModel}>
                    Retry Loading
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <ThreeViewer
              modelData={modelData}
              analysisData={analysisData}
              onExport={handleExportPLY}
            />
          )}
        </div>

        <div className="space-y-4">
          {/* Detailed Metrics */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Detailed Analysis</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Avg Depth:</span>
                  <p className="font-medium">{metrics.avg_depth_mm || '--'} mm</p>
                </div>
                <div>
                  <span className="text-gray-600">Perimeter:</span>
                  <p className="font-medium">{metrics.perimeter_cm || '--'} cm</p>
                </div>
                <div>
                  <span className="text-gray-600">Healing Stage:</span>
                  <p className="font-medium">{metrics.healing_stage || 'Unknown'}</p>
                </div>
                <div>
                  <span className="text-gray-600">Analysis Quality:</span>
                  <p className="font-medium">
                    <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                      (metrics.confidence || 0) > 80 ? 'bg-green-500' :
                      (metrics.confidence || 0) > 60 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}></span>
                    {(metrics.confidence || 0) > 80 ? 'High' : 
                     (metrics.confidence || 0) > 60 ? 'Medium' : 'Low'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Burn Classification */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Burn Classification</CardTitle>
            </CardHeader>
            <CardContent>
              <ClassificationChart classifications={metrics.classifications} />
            </CardContent>
          </Card>

          {/* Clinical Recommendations */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Clinical Insights</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                {metrics.data_source === 'Awaiting Advanced AI Analysis' ? (
                  <div className="bg-gray-50 border border-gray-200 rounded p-3">
                    <p className="text-gray-800 font-medium">🔬 Analysis Required</p>
                    <p className="text-gray-700 text-xs mt-1">Run Advanced AI Analysis to get clinical insights and recommendations</p>
                  </div>
                ) : (
                  <>
                    {metrics.healing_stage === 'No burn detected' && (
                      <div className="bg-green-50 border border-green-200 rounded p-3">
                        <p className="text-green-800 font-medium">✅ No burn detected</p>
                        <p className="text-green-700 text-xs mt-1">AI analysis found no visible burn or injury</p>
                      </div>
                    )}
                    
                    {metrics.healing_stage && metrics.healing_stage.includes('Full thickness') && (
                      <div className="bg-red-50 border border-red-200 rounded p-3">
                        <p className="text-red-800 font-medium">⚠️ Deep burn detected</p>
                        <p className="text-red-700 text-xs mt-1">Consider surgical intervention</p>
                      </div>
                    )}
                    
                    {(metrics.tbsa_percentage && metrics.tbsa_percentage > 20) && (
                      <div className="bg-orange-50 border border-orange-200 rounded p-3">
                        <p className="text-orange-800 font-medium">🏥 Large burn area</p>
                        <p className="text-orange-700 text-xs mt-1">Monitor for complications</p>
                      </div>
                    )}
                    
                    {metrics.data_source?.includes('Advanced') && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-3">
                        <p className="text-blue-800 font-medium">📊 AI Assessment Complete</p>
                        <p className="text-blue-700 text-xs mt-1">Schedule follow-up based on burn severity</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Image Overlay Modal */}
      {showImageOverlay && (
        <ImageOverlay
          sessionId={sessionId}
          modelData={modelData}
          onClose={() => setShowImageOverlay(false)}
        />
      )}

      {/* Advanced Analysis Processing Overlay */}
      {advancedAnalysisLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
            <div className="w-16 h-16 border-4 border-blue-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
            <h3 className="text-xl font-bold mb-2 flex items-center justify-center gap-2">
              <Brain className="w-6 h-6 text-blue-600" />
              Advanced AI Analysis
            </h3>
            <p className="text-gray-600 mb-2">Processing your burn analysis with AI...</p>
            <p className="text-sm text-gray-500">This may take 1-2 minutes to complete</p>
            <div className="mt-4 text-xs text-gray-400">
              Please do not close this window or refresh the page
            </div>
          </div>
        </div>
      )}

      {/* Advanced Analysis Results now displayed inline above */}
      {false && showAdvancedResults && advancedAnalysisResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Brain className="w-6 h-6 text-blue-600" />
                  Advanced AI Analysis Results
                </h3>
                <Button
                  variant="outline"
                  onClick={() => setShowAdvancedResults(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </Button>
              </div>

              <div className="space-y-6">
                {/* Mesh Analysis Results */}
                {advancedAnalysisResults.mesh && (
                  <div>
                    <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <Layers className="w-5 h-5 text-green-600" />
                      3D Mesh Analysis
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm space-y-2">
                        <div><strong>Status:</strong> {advancedAnalysisResults.mesh.result?.success ? 'Success' : 'Failed'}</div>
                        <div><strong>Burn Detected:</strong> {advancedAnalysisResults.mesh.result?.has_burn ? 'Yes' : 'No'}</div>
                        <div><strong>Analysis Type:</strong> {advancedAnalysisResults.mesh.result?.analysis_type}</div>
                      </div>
                      {advancedAnalysisResults.mesh.result?.overlay_filename && (
                        <div className="mt-4">
                          <h5 className="font-medium mb-2">Generated 3D Mesh Overlay:</h5>
                          <div className="bg-white rounded p-3">
                            <img 
                              src={`${API}/sessions/${sessionId}/images/${advancedAnalysisResults.mesh.result.overlay_filename}`}
                              alt="3D Mesh Overlay"
                              className="max-w-full h-auto rounded border"
                              onError={(e) => {
                                console.error('Failed to load mesh overlay image');
                                e.target.style.display = 'none';
                              }}
                            />
                            <p className="text-xs text-gray-500 mt-2">
                              SKINOPATHY-generated 3D mesh overlay with burn area analysis
                            </p>
                          </div>
                        </div>
                      )}
                      {advancedAnalysisResults.mesh.result?.mesh_analysis && (
                        <div className="mt-4">
                          <h5 className="font-medium mb-2">Analysis Notes:</h5>
                          <div className="bg-white rounded p-3 text-sm whitespace-pre-wrap">
                            {advancedAnalysisResults.mesh.result.mesh_analysis}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Quantitative Analysis Results */}
                {advancedAnalysisResults.quantitative && (
                  <div>
                    <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-purple-600" />
                      Quantitative Analysis
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm space-y-2">
                        <div><strong>Status:</strong> {advancedAnalysisResults.quantitative.result?.success ? 'Success' : 'Failed'}</div>
                        <div><strong>Burn Detected:</strong> {advancedAnalysisResults.quantitative.result?.has_burn ? 'Yes' : 'No'}</div>
                        <div><strong>Analysis Type:</strong> {advancedAnalysisResults.quantitative.result?.analysis_type}</div>
                      </div>
                      
                      {/* Display detailed burn analysis data */}
                      {advancedAnalysisResults.quantitative.result?.analysis_data && advancedAnalysisResults.quantitative.result.analysis_data.has_burn && (
                        <div className="mt-4 space-y-4">
                          
                          {/* Quantitative Measurements Table */}
                          <div>
                            <h5 className="font-medium mb-2 text-blue-800">Quantitative Burn Analysis</h5>
                            <div className="overflow-x-auto">
                              <table className="min-w-full bg-white border border-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-3 py-2 text-left border-b">Metric</th>
                                    <th className="px-3 py-2 text-left border-b">Value</th>
                                    <th className="px-3 py-2 text-left border-b">Units</th>
                                    <th className="px-3 py-2 text-left border-b">Description</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr className="border-b">
                                    <td className="px-3 py-2 font-medium">Surface Area</td>
                                    <td className="px-3 py-2">{advancedAnalysisResults.quantitative.result.analysis_data.SurfaceArea_cm2}</td>
                                    <td className="px-3 py-2">cm²</td>
                                    <td className="px-3 py-2">Total visible affected area</td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="px-3 py-2 font-medium">Volume</td>
                                    <td className="px-3 py-2">{advancedAnalysisResults.quantitative.result.analysis_data.Volume_cm3}</td>
                                    <td className="px-3 py-2">cm³</td>
                                    <td className="px-3 py-2">Estimated wound cavity volume</td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="px-3 py-2 font-medium">Max Depth</td>
                                    <td className="px-3 py-2">{advancedAnalysisResults.quantitative.result.analysis_data.MaxDepth_mm}</td>
                                    <td className="px-3 py-2">mm</td>
                                    <td className="px-3 py-2">Maximum visible tissue depression</td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="px-3 py-2 font-medium">Avg Depth</td>
                                    <td className="px-3 py-2">{advancedAnalysisResults.quantitative.result.analysis_data.AvgDepth_mm}</td>
                                    <td className="px-3 py-2">mm</td>
                                    <td className="px-3 py-2">Average surface depression</td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="px-3 py-2 font-medium">Perimeter</td>
                                    <td className="px-3 py-2">{advancedAnalysisResults.quantitative.result.analysis_data.Perimeter_cm}</td>
                                    <td className="px-3 py-2">cm</td>
                                    <td className="px-3 py-2">Boundary length of affected region</td>
                                  </tr>
                                  <tr>
                                    <td className="px-3 py-2 font-medium">TBSA Percentage</td>
                                    <td className="px-3 py-2">{advancedAnalysisResults.quantitative.result.analysis_data.TBSA_percent}%</td>
                                    <td className="px-3 py-2">%</td>
                                    <td className="px-3 py-2">Total Body Surface Area affected</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Burn Classification Table */}
                          {advancedAnalysisResults.quantitative.result.analysis_data.BurnClassification && (
                            <div>
                              <h5 className="font-medium mb-2 text-purple-800">Burn Classification Distribution</h5>
                              <div className="overflow-x-auto">
                                <table className="min-w-full bg-white border border-gray-200 text-sm">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-3 py-2 text-left border-b">Burn Type</th>
                                      <th className="px-3 py-2 text-left border-b">Percentage</th>
                                      <th className="px-3 py-2 text-left border-b">Clinical Notes</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr className="border-b">
                                      <td className="px-3 py-2 font-medium">Superficial (1°)</td>
                                      <td className="px-3 py-2">{advancedAnalysisResults.quantitative.result.analysis_data.BurnClassification.Superficial}%</td>
                                      <td className="px-3 py-2">Red, intact epidermis, no blisters</td>
                                    </tr>
                                    <tr className="border-b">
                                      <td className="px-3 py-2 font-medium">Partial-thickness (2°)</td>
                                      <td className="px-3 py-2">{advancedAnalysisResults.quantitative.result.analysis_data.BurnClassification.PartialThickness}%</td>
                                      <td className="px-3 py-2">Glossy red, moist, blistering, viable dermis</td>
                                    </tr>
                                    <tr>
                                      <td className="px-3 py-2 font-medium">Full-thickness (3°)</td>
                                      <td className="px-3 py-2">{advancedAnalysisResults.quantitative.result.analysis_data.BurnClassification.FullThickness}%</td>
                                      <td className="px-3 py-2">Charred, leathery, waxy white or black regions</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* Diagnostic Reasoning Table */}
                          {advancedAnalysisResults.quantitative.result.analysis_data.diagnostic_reasoning && advancedAnalysisResults.quantitative.result.analysis_data.diagnostic_reasoning.length > 0 && (
                            <div>
                              <h5 className="font-medium mb-2 text-green-800">Diagnostic Reasoning</h5>
                              <div className="overflow-x-auto">
                                <table className="min-w-full bg-white border border-gray-200 text-sm">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-3 py-2 text-left border-b">Feature Observed</th>
                                      <th className="px-3 py-2 text-left border-b">Interpretation</th>
                                      <th className="px-3 py-2 text-left border-b">Burn Depth Implication</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {advancedAnalysisResults.quantitative.result.analysis_data.diagnostic_reasoning.map((item, index) => (
                                      <tr key={index} className={index < advancedAnalysisResults.quantitative.result.analysis_data.diagnostic_reasoning.length - 1 ? "border-b" : ""}>
                                        <td className="px-3 py-2">{item.Feature}</td>
                                        <td className="px-3 py-2">{item.Interpretation}</td>
                                        <td className="px-3 py-2">{item.BurnDepthImplication}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* Clinical Summary */}
                          {advancedAnalysisResults.quantitative.result.analysis_data.ClinicalSummary && (
                            <div>
                              <h5 className="font-medium mb-2 text-red-800">Clinical Summary</h5>
                              <div className="bg-red-50 border border-red-200 rounded p-3 text-sm">
                                <p className="font-medium text-red-900">{advancedAnalysisResults.quantitative.result.analysis_data.ClinicalSummary}</p>
                              </div>
                            </div>
                          )}

                        </div>
                      )}

                      {/* Show message when no burn detected */}
                      {advancedAnalysisResults.quantitative.result?.analysis_data && !advancedAnalysisResults.quantitative.result.analysis_data.has_burn && (
                        <div className="mt-4">
                          <div className="bg-green-50 border border-green-200 rounded p-3 text-sm">
                            <p className="text-green-800 font-medium">✅ No burn or injury detected in the analyzed image.</p>
                            <p className="text-green-700 mt-1">The AI analysis did not identify any visible burns, wounds, or lesions requiring medical assessment.</p>
                          </div>
                        </div>
                      )}

                      {advancedAnalysisResults.quantitative.result?.quantitative_analysis && (
                        <div className="mt-4">
                          <h5 className="font-medium mb-2">Full Analysis:</h5>
                          <div className="bg-white rounded p-3 text-sm whitespace-pre-wrap max-h-60 overflow-y-auto">
                            {advancedAnalysisResults.quantitative.result.quantitative_analysis}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="text-xs text-gray-500 text-center">
                  Analysis completed at: {new Date(advancedAnalysisResults.timestamp).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisView;